import React, { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'admin'
  });
  const [formLoading, setFormLoading] = useState(false);

  const { admin, authenticatedFetch } = useAdminAuth();


  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/admin/users');
      const data = await response.json();
      
      if (data.success) {
        setUsers(data.admins);
        setError('');
      } else {
        setError(data.error);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);  // Agregar fetchUsers como dependencia

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const response = await authenticatedFetch('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setShowForm(false);
        setFormData({ email: '', name: '', password: '', role: 'admin' });
        await fetchUsers();
        alert('Usuario creado exitosamente');
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Error de conexión');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      const response = await authenticatedFetch(`/api/admin/users/${userId}/toggle`, {
        method: 'PATCH'
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchUsers();
      } else {
        alert('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      alert('Error de conexión');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAuthMethodBadge = (method) => {
    if (method === 'google') {
      return { text: 'Google', color: '#4285f4' };
    }
    return { text: 'Email', color: '#667eea' };
  };

  const getRoleBadge = (role) => {
    if (role === 'super_admin') {
      return { text: 'Super Admin', color: '#e53e3e' };
    }
    return { text: 'Admin', color: '#48bb78' };
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e2e8f0',
          borderTop: '4px solid #667eea',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '16px'
        }}></div>
        <p style={{ color: '#718096', margin: '0' }}>
          Cargando usuarios...
        </p>
        <style>
          {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
        </style>
      </div>
    );
  }

  // Verificar permisos
  if (admin?.role !== 'super_admin') {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        background: 'white',
        borderRadius: '12px',
        border: '2px solid #fed7d7'
      }}>
        <h3 style={{ color: '#c53030', marginBottom: '10px' }}>
          Acceso Restringido
        </h3>
        <p style={{ color: '#718096', margin: '0' }}>
          Solo los Super Administradores pueden gestionar usuarios.
        </p>
      </div>
    );
  }

  return (
    <div style={{ background: '#f8f9fa', minHeight: '100vh', padding: '20px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <div>
            <h2 style={{
              color: '#4a5568',
              margin: '0 0 5px 0',
              fontSize: '1.8em',
              fontWeight: '600'
            }}>
              Gestión de Usuarios
            </h2>
            <p style={{ color: '#718096', margin: '0' }}>
              Administrar usuarios con acceso al panel administrativo
            </p>
          </div>
          <button 
            onClick={() => setShowForm(true)}
            style={{
              background: '#48bb78',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#38a169';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#48bb78';
              e.target.style.transform = 'none';
            }}
          >
            + Nuevo Usuario
          </button>
        </div>

        {error && (
          <div style={{
            background: '#fed7d7',
            border: '1px solid #feb2b2',
            color: '#c53030',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {/* Modal de formulario */}
        {showForm && (
          <div style={{
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: '1000',
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h3 style={{
                margin: '0 0 20px 0',
                color: '#4a5568',
                fontSize: '1.4em'
              }}>
                Crear Nuevo Usuario
              </h3>

              <div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#4a5568'
                  }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                    placeholder="admin@ejemplo.com"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '1em'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#4a5568'
                  }}>
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    placeholder="Juan Pérez"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '1em'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#4a5568'
                  }}>
                    Contraseña *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleFormChange}
                    required
                    placeholder="Mínimo 6 caracteres"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '1em'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '30px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#4a5568'
                  }}>
                    Rol
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleFormChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '1em'
                    }}
                  >
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '10px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    style={{
                      padding: '10px 20px',
                      border: '1px solid #e2e8f0',
                      background: 'white',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCreateUser}
                    disabled={formLoading}
                    style={{
                      padding: '10px 20px',
                      background: formLoading ? '#a0aec0' : '#48bb78',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: formLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {formLoading ? 'Creando...' : 'Crear Usuario'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lista de usuarios */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)'
        }}>
          {users.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px'
            }}>
              <p style={{
                color: '#718096',
                fontSize: '1.1em',
                marginBottom: '20px'
              }}>
                No hay usuarios registrados
              </p>
              <button 
                onClick={() => setShowForm(true)}
                style={{
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Crear primer usuario
              </button>
            </div>
          ) : (
            <div>
              {/* Header de tabla */}
              <div style={{
                background: '#f8f9fa',
                padding: '15px 20px',
                borderBottom: '1px solid #e2e8f0',
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto',
                gap: '15px',
                fontWeight: '600',
                color: '#4a5568',
                fontSize: '0.9em'
              }}>
                <div>Usuario</div>
                <div>Método</div>
                <div>Rol</div>
                <div>Estado</div>
                <div>Último acceso</div>
                <div>Acciones</div>
              </div>

              {/* Filas de usuarios */}
              {users.map((user) => {
                const authBadge = getAuthMethodBadge(user.auth_method);
                const roleBadge = getRoleBadge(user.role);
                
                return (
                  <div 
                    key={user.id}
                    style={{
                      padding: '20px',
                      borderBottom: '1px solid #e2e8f0',
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto',
                      gap: '15px',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{
                        fontWeight: '600',
                        color: '#4a5568',
                        marginBottom: '4px'
                      }}>
                        {user.name}
                      </div>
                      <div style={{
                        color: '#718096',
                        fontSize: '0.9em'
                      }}>
                        {user.email}
                      </div>
                    </div>

                    <div>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '0.75em',
                        fontWeight: '600',
                        color: 'white',
                        background: authBadge.color
                      }}>
                        {authBadge.text}
                      </span>
                    </div>

                    <div>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '0.75em',
                        fontWeight: '600',
                        color: 'white',
                        background: roleBadge.color
                      }}>
                        {roleBadge.text}
                      </span>
                    </div>

                    <div>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '0.75em',
                        fontWeight: '600',
                        background: user.is_active ? '#c6f6d5' : '#fed7d7',
                        color: user.is_active ? '#22543d' : '#742a2a'
                      }}>
                        {user.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>

                    <div style={{
                      color: '#718096',
                      fontSize: '0.85em'
                    }}>
                      {user.last_login ? formatDate(user.last_login) : 'Nunca'}
                    </div>

                    <div>
                      {user.id !== admin.id && (
                        <button 
                          onClick={() => handleToggleStatus(user.id)}
                          style={{
                            padding: '6px 12px',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.8em',
                            fontWeight: '500',
                            background: user.is_active ? '#ed8936' : '#48bb78',
                            color: 'white'
                          }}
                        >
                          {user.is_active ? 'Desactivar' : 'Activar'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;